import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router';
import { Switch, Route } from 'react-router-dom';

import { Brand } from 'apis/enterprise/types';
import { MerchantDetail } from 'apis/restaurant-api/merchant-admin/merchant/types';
import useUser from 'common/hooks/useUser';
import { usePermissions } from 'common/wolt-permissions';
import {
  GOOGLE_SHEETS_PAGE_PATH,
  BULK_IMAGE_UPLOAD_PAGE_PATH,
  TRANSLATIONS_PAGE_PATH,
  IMAGE_GALLERY_PAGE_PATH,
} from 'modules/shared-menu/constants';
import { OfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { OfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { OnboardingStepsProvider } from 'modules/shared-menu/contexts/OnboardingStepsContext';
import { SelectionInfoContextProvider } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useIsListingManagerEditingEnabled } from 'modules/shared-menu/hooks/useIsListingManagerEditingEnabled';

import { SHARED_MENU_DEFAULT_FEATURES } from './constants';
import { buildMerchantBrandListingManagerPermissions } from './utils';
import { VenueWithSharedMenuLinkInfo } from '../../hooks/useVenuesWithSelectionLinks';
import { SharedMenu } from '../../types';
import { GoogleSheetsImportPage } from '../GoogleSheetsImportPage';
import { ListingManagerImageGalleryPage } from '../ListingManagerImageGalleryPage/ListingManagerImageGalleryPage';
import { SharedMenuBulkImageUploadPage } from '../SharedMenuBulkImageUploadPage';
import { SharedMenuEditorPage } from '../SharedMenuEditorPage';
import { WoltSpecialsContextProvider } from '../SharedMenuEditorPage/context/WoltSpecialsContext';
import { SharedMenuTranslationsPage } from '../SharedMenuTranslationsPage';

type Props = {
  allVenues: VenueWithSharedMenuLinkInfo[];
  sharedMenu: SharedMenu;
  country: Country;
  brand?: Brand;
  merchant?: MerchantDetail;
};

export const SharedMenuRoot = ({ allVenues, sharedMenu, country, brand, merchant }: Props) => {
  const { isEmployee } = useUser();
  const { path } = useRouteMatch();
  const permissions = usePermissions();
  const { isListingManagerEditingEnabled } = useIsListingManagerEditingEnabled();

  const listingManagerPermissions = useMemo(
    () => buildMerchantBrandListingManagerPermissions(permissions),
    [permissions],
  );

  return (
    <SelectionInfoContextProvider
      value={{
        country,
        selection: sharedMenu,
        listingManagerConfig: {
          brand,
          merchant,
        },
      }}
    >
      <OfferingMenuPermissionsContext.Provider
        value={{
          ...listingManagerPermissions,
          contentEdit: isListingManagerEditingEnabled && listingManagerPermissions.contentEdit,
        }}
      >
        <OfferingMenuFeaturesContext.Provider
          value={{
            ...SHARED_MENU_DEFAULT_FEATURES,
            externalHelpCenterLinkEnabled: !isEmployee && country === 'EST',
          }}
        >
          <Switch>
            {listingManagerPermissions.contentEdit && (
              <Route path={`${path}/menu-editor/${GOOGLE_SHEETS_PAGE_PATH}`} exact>
                <WoltSpecialsContextProvider>
                  <GoogleSheetsImportPage sharedMenu={sharedMenu} />
                </WoltSpecialsContextProvider>
              </Route>
            )}
            <Route path={`${path}/menu-editor/${BULK_IMAGE_UPLOAD_PAGE_PATH}`} exact>
              <SharedMenuBulkImageUploadPage sharedMenu={sharedMenu} />
            </Route>
            <Route path={`${path}/menu-editor/${IMAGE_GALLERY_PAGE_PATH}`}>
              <ListingManagerImageGalleryPage />
            </Route>
            <Route path={`${path}/menu-editor/${TRANSLATIONS_PAGE_PATH}`} exact>
              <SharedMenuTranslationsPage />
            </Route>
            <OnboardingStepsProvider>
              <Route path={`${path}/menu-editor`}>
                <SharedMenuEditorPage sharedMenu={sharedMenu} venuesWithLinks={allVenues} />
              </Route>
            </OnboardingStepsProvider>
          </Switch>
        </OfferingMenuFeaturesContext.Provider>
      </OfferingMenuPermissionsContext.Provider>
    </SelectionInfoContextProvider>
  );
};
